import React, { useState } from 'react'
import { CButton, CCard, CCardBody, CCol, CForm, CInputGroup, CRow } from '@coreui/react'
import BaseListing from 'src/BaseListings'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import Loading from 'src/helpers/commons/loader'
import WidgetDatePicker from 'src/widgets/DatePickers/WidgetDatePicker'
import { wtGetDateToday } from 'src/helpers/commons/moment'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import WidgetDepartmentSelector from 'src/widgets/Selectors/WidgetDepartmentSelector'
import WidgetDesignationSelector from 'src/widgets/Selectors/WidgetDesignationSelector'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import moment from 'moment'

export default function EmployeeAbsentReport() {
  const Base = new BaseListing()
  const [asOf, setAsOf] = useState(wtGetDateToday())
  const [reportData, setReportData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [branches, setBranches] = useState([])
  const [departments, setDepartments] = useState([])
  const [designations, setDesignations] = useState([])
  const [branchId, setBranchId] = useState(0)
  const [departmentId, setDepartmentId] = useState(0)
  const [designationId, setDesignationId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [departmentName, setDepartmentName] = useState('')
  const [designationName, setDesignationName] = useState('')

  React.useEffect(() => {
    Base.dropdownData(
      'branches,departments,designations',
      {},
      [setBranches, setDepartments, setDesignations],
      1,
    )
  }, [])

  const getReportData = () => {
    setIsLoading(true)
    wtApiCall(
      'reports/employee_absent_report',
      {
        as_of: asOf,
        branch_id: branchId,
        department_id: departmentId,
        designation_id: designationId,
      },
      'get',
      (response) => {
        setReportData(response.data || [])
        setHasSearched(true)
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const handleBranchChange = (value) => {
    setBranchId(value.value)
    setBranchName(value.label)
    setDepartmentId(0)
    setDepartmentName('')
    Base.dropdownData('departments', { branch_id: value.value }, [setDepartments], 1)
  }

  const generatePDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const title = `Unpresented Employees${branchName ? ` - ${branchName}` : ''}`
    const reportDate = `As of ${moment(asOf).format('MMMM DD, YYYY')}`
    const logo = 'assets/wil.png'

    const drawPageHeader = () => {
      doc.addImage(logo, 'PNG', 5, 1, 62, 34)
      doc.setDrawColor(60, 60, 60)
      doc.setLineWidth(0.4)
      doc.line(10, 35, 200, 35)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(13)
      doc.setTextColor(20, 20, 20)
      doc.text(title, 200, 13, { align: 'right' })
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.setTextColor(37, 99, 235)
      doc.text(reportDate, 200, 22, { align: 'right' })
    }

    doc.autoTable({
      startY: 40,
      head: [['Sr#', 'Emp ID', 'Name', 'Department', 'Designation', 'Status']],
      body: reportData.map((item, index) => [
        index + 1,
        item.employee?.id || '-',
        employeeName(item.employee) || '-',
        item.employee?.department?.name || '-',
        item.employee?.designation?.name || '-',
        'Absent',
      ]),
      styles: {
        font: 'helvetica',
        fontSize: 8.5,
        cellPadding: { top: 1.8, right: 2.5, bottom: 1.8, left: 2.5 },
        valign: 'middle',
        lineColor: [203, 213, 225],
        lineWidth: 0.25,
        textColor: [30, 41, 59],
      },
      headStyles: {
        fillColor: [226, 232, 240],
        textColor: [15, 23, 42],
        fontStyle: 'bold',
        halign: 'center',
        lineColor: [148, 163, 184],
        lineWidth: 0.3,
      },
      columnStyles: {
        0: { cellWidth: 11, halign: 'center' },
        1: { cellWidth: 21, halign: 'center' },
        2: { cellWidth: 37 },
        3: { cellWidth: 43 },
        4: { cellWidth: 48 },
        5: { cellWidth: 30, halign: 'center', textColor: [217, 119, 6], fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { top: 36, bottom: 18, left: 10, right: 10 },
      didDrawPage: () => {
        drawPageHeader()
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(37, 99, 235)
        doc.text(
          `This report is generated through ERP at ${moment().format('YYYY-MM-DD HH:mm:ss')}`,
          105,
          290,
          { align: 'center' },
        )
      },
    })
    doc.save(`Absent_Report_${moment(asOf).format('YYYY_MM_DD')}.pdf`)
  }

  const employeeName = (employee) =>
    employee?.full_name || [employee?.first_name, employee?.last_name].filter(Boolean).join(' ')

  return (
    <div style={{ padding: '2%' }}>
      <center><h4>Absent Report</h4></center>

      <CRow>
        <CCol md={12}>
          <CCard className="card-dark">
            <CCardBody style={{ backgroundColor: '#f7f9fa' }}>
              <CForm>
                <CRow>
                  <CCol md={3}>
                    <CInputGroup>
                      <label>As of</label>
                      <WidgetDatePicker
                        value={asOf}
                        setDate={setAsOf}
                        placeholder="Select date"
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={3}>
                    <CInputGroup>
                      <label>Branch</label>
                      <WidgetBranchSelector
                        branches={branches}
                        displayValue={branchName}
                        value={branchId}
                        placeholder="Select Branch"
                        onChange={handleBranchChange}
                        reloadDropdownData={() => Base.dropdownData('branches', {}, [setBranches], 1)}
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={3}>
                    <CInputGroup>
                      <label>Department</label>
                      <WidgetDepartmentSelector
                        departments={departments}
                        displayValue={departmentName}
                        value={departmentId}
                        placeholder="Select Department"
                        onChange={(value) => {
                          setDepartmentId(value.value)
                          setDepartmentName(value.label)
                        }}
                        reloadDropdownData={() => Base.dropdownData('departments', {}, [setDepartments], 1)}
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={3}>
                    <CInputGroup>
                      <label>Designation</label>
                      <WidgetDesignationSelector
                        designations={designations}
                        displayValue={designationName}
                        value={designationId}
                        placeholder="Select Designation"
                        onChange={(value) => {
                          setDesignationId(value.value)
                          setDesignationName(value.label)
                        }}
                        reloadDropdownData={() => Base.dropdownData('designations', {}, [setDesignations], 1)}
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={3} style={{ marginTop: '20px' }}>
                    <CButton color="primary" onClick={getReportData}>
                      Search
                    </CButton>
                    <CButton color="danger" className="text-white" onClick={generatePDF} disabled={!reportData.length} style={{ marginLeft: '8px' }}>
                      PDF
                    </CButton>
                  </CCol>
                </CRow>
              </CForm>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <br />
      {isLoading ? Loading : (
        reportData.length > 0 ? (
          <table className="table table-bordered data-table">
            <thead className="thead-dark" style={{ backgroundColor: '#e8e9eb' }}>
              <tr>
                <th>Sr#</th>
                <th>Emp ID</th>
                <th>Name</th>
                <th>Department</th>
                <th>Designation</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>{item.employee?.id}</td>
                  <td>{employeeName(item.employee)}</td>
                  <td>{item.employee?.department?.name || '-'}</td>
                  <td>{item.employee?.designation?.name || '-'}</td>
                  <td style={{ color: 'red', fontWeight: 'bold' }}>Absent</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : hasSearched ? (
          <center style={{ fontSize: '18px', fontWeight: 'bold' }}>No record to show...</center>
        ) : null
      )}
    </div>
  )
}
